import { setTaskFilter } from '../../ui/note-task-filter.js';
import { cloneDoc, exportMap, handleExportData, chooseVaultImport } from '../../ui/vault/actions.js';
import { moveToFolder } from '../../ui/vault/dialogs.js';
import { pickAttachmentFile, pickAttachmentPhoto } from "../attachments.js";
import { handleTodayAction } from "./today-actions.js";
import { toggleTagSelection } from '../../../../engine/core/node-tags.js';
import { ESTIMATE_UNITS } from '../../../../engine/core/estimate.js';
import { chooseLanguage } from '../../ui/language-button.js';
import { t } from '../../../../shared/i18n/index.js';
import { canOpenLinkedMap, canRequestLinkAccess } from '../../../../engine/sync/link-state.js';

export function handleMobileActionClick(event, deps) {
  const {
    callbacks,
    container,
    uiState,
    isNodeLocked = () => callbacks.Store.isReadOnly?.(),
    addNodeFromForm,
    adjustGanttRowHeight,
    beginEditNode,
    beginEditNote,
    beginInlineRename,
    clearNodeEditDraft,
    clearNoteEditDraft,
    closeTransientSheets,
    collapseAllNodes,
    collapseSelectedChildren,
    createDocFromForm,
    createFolderFromForm,
    createNote,
    ensurePendingEditsSavedBeforeNavigation,
    expandAllNodes,
    fitMountedDesktopView,
    cycleMindmapLayoutMode,
    getSuppressNextRowClick,
    handleBack,
    handleTitleTap,
    moveDocToFolder,
    moveNodeToParent,
    navigateUpFolder,
    openAddNodeForm,
    openAddNodeToSelectedForm,
    openDoc,
    openNode,
    openRefnote,
    openSelectedProperties,
    promptInstall,
    renameDocFromForm,
    renameFolderFromForm,
    renameNodeFromForm,
    requestCancelEditNote,
    requestCloseNodePanel,
    requestDeleteDoc,
    requestDeleteFolder,
    requestDeleteNode,
    requestDeleteNote,
    runConfirmAction,
    saveNode,
    saveNote,
    scheduleRender,
    selectOutlineNode,
    setCardStatus,
    setNodeDatePreset,
    setSuppressNextRowClick,
    snapshotNodeEditDraftFromDom,
    toggleCollapse,
    toggleNodeTagFromButton,
    toggleStoreIdList,
  } = deps;

  const actionEl = event.target.closest("[data-mobile-action]");
  if (!actionEl || !container?.contains(actionEl)) return;
  // Long-press just opened a sheet; swallow the synthetic click that follows
  // touchend so the same row does not also select/open.
  if (getSuppressNextRowClick()) {
    setSuppressNextRowClick(false);
    if (actionEl.closest(".mobile-outline-row")) {
      event.preventDefault();
      return;
    }
  }

  const action = actionEl.dataset.mobileAction;
  if (!["docs-search", "note-search", "global-search"].includes(action)) {
    event.preventDefault();
  }

  // Today list (execution model) — see actions/today-actions.js
  if (action.startsWith("today-")) {
    handleTodayAction(action, actionEl, deps);
    return;
  }

  switch (action) {
    case 'add-node-tag': {
      if (uiState.form?.type !== 'add-node') break;
      const defs = callbacks.sharedCallbacks.getTagDefinitions?.(uiState.form.parentId) || [];
      uiState.form.tagIds = toggleTagSelection(uiState.form.tagIds, actionEl.dataset.tagId, defs);
      scheduleRender();
      break;
    }
    case 'add-node-estimate-unit': {
      if (uiState.form?.type !== 'add-node') break;
      const unit = actionEl.dataset.unit;
      const spec = ESTIMATE_UNITS[unit];
      uiState.form.estimate = spec ? {unit, value: Math.min(uiState.form.estimate?.value || 1, spec.max)} : null;
      scheduleRender();
      break;
    }
    case "toggle-vault": {
      if (!ensurePendingEditsSavedBeforeNavigation()) break;
      if (uiState.tab === "docs" && callbacks.Store.getState()?.data) {
        // Exit the vault browser back to the current map.
        uiState.tab = uiState.lastContentTab || "outline";
      } else {
        if (uiState.tab !== "docs") uiState.lastContentTab = uiState.tab;
        uiState.tab = "docs";
      }
      uiState.activeNodeId = null;
      uiState.nodeEditing = false;
      uiState.editingNodeContext = null;
      clearNodeEditDraft();
      uiState.filterOpen = false;
      closeTransientSheets();
      scheduleRender();
      break;
    }
    case "nav":
      if (!ensurePendingEditsSavedBeforeNavigation()) break;
      if (uiState.tab !== "docs") uiState.lastContentTab = uiState.tab;
      uiState.tab = actionEl.dataset.tab || "outline";
      uiState.activeNodeId = null;
      uiState.nodeEditing = false;
      uiState.editingNodeContext = null;
      clearNodeEditDraft();
      uiState.filterOpen = false;
      closeTransientSheets();
      scheduleRender();
      break;
    case "back":
      handleBack();
      break;
    case "login":
      callbacks.showLogin?.();
      break;
    case "open-search":
      uiState.searchOpen = true;
      scheduleRender();
      break;
    case "close-search":
      uiState.searchOpen = false;
      scheduleRender();
      break;
    case "open-user-info":
      uiState.userInfoOpen = true;
      scheduleRender();
      break;
    case "close-user-info":
      uiState.userInfoOpen = false;
      scheduleRender();
      break;
    // Document properties, opened from the document name in the top bar.
    case "open-doc-props":
      uiState.docPropsOpen = true;
      deps.loadDocShareInfo?.();
      scheduleRender();
      break;
    case "close-doc-props":
      uiState.docPropsOpen = false;
      uiState.docShareInfo = null;
      scheduleRender();
      break;
    case "apply-search":
      uiState.searchQuery = document.getElementById("mobile-global-search")?.value || "";
      scheduleRender();
      break;
    case "search-open-node":
      if (!ensurePendingEditsSavedBeforeNavigation()) break;
      uiState.searchOpen = false;
      openNode(actionEl.dataset.nodeId);
      break;
    case "search-open-note":
      if (!ensurePendingEditsSavedBeforeNavigation()) break;
      setTaskFilter(null, true);
      uiState.notesSearchQuery = "";
      uiState.searchOpen = false;
      uiState.tab = "notes";
      uiState.activeNoteId = actionEl.dataset.noteId;
      uiState.activeNoteDocId = actionEl.dataset.noteDocId || null;
      uiState.noteSearchReveal = true;
      uiState.noteEditing = false;
      uiState.editingNoteContext = null;
      clearNoteEditDraft();
      scheduleRender();
      break;
    case "open-refnote":
      openRefnote(actionEl.dataset.nodeId, actionEl.dataset.linkedDocId || null);
      break;
    case "open-filter":
      uiState.filterOpen = true;
      scheduleRender();
      break;
    case "open-history":
      // The map's history: the same dialog as on desktop (it fits a phone).
      uiState.docPropsOpen = false;
      scheduleRender();
      void import("../../ui/history.js").then((History) => History.open(actionEl.dataset.docId));
      break;
    case "close-filter":
      uiState.filterOpen = false;
      scheduleRender();
      break;
    case "clear-filters":
      callbacks.Store.setHighlightedTagIds([]);
      callbacks.Store.setHiddenTagIds([]);
      uiState.filterOpen = false;
      break;
    case "toggle-highlight-tag":
      toggleStoreIdList("highlightedTagIds", actionEl.dataset.tagId);
      break;
    case "quick-filter-tag": {
      const id = actionEl.dataset.tagId;
      if (id) toggleStoreIdList("highlightedTagIds", id);
      break;
    }
    case "toggle-hidden-tag":
    case "toggle-show-tag":
      // Both actions hit the same store list. The new "Show" button shows
      // its active state when the tag is NOT in hiddenTagIds, so a click
      // toggles visibility — exactly the inverse of the legacy "Hide"
      // button that flipped the same list.
      toggleStoreIdList("hiddenTagIds", actionEl.dataset.tagId);
      break;
    case "select-outline-node":
      selectOutlineNode(actionEl.dataset.nodeId);
      break;
    case "outline-deselect":
      // Only deselect when the click landed on the empty wrapper itself.
      if (actionEl === event.target) {
        callbacks.Store.setSelected?.(null);
        scheduleRender();
      }
      break;
    case "title-tap":
      handleTitleTap(actionEl.dataset.nodeId);
      break;
    case "open-node-properties":
      openNode(actionEl.dataset.nodeId);
      break;
    case "rename-node":
      if (isNodeLocked(actionEl.dataset.nodeId)) break;
      beginInlineRename(actionEl.dataset.nodeId);
      break;
    case "open-rename-node-form":
      if (isNodeLocked(actionEl.dataset.nodeId)) break;
      uiState.form = { type: "rename-node", nodeId: actionEl.dataset.nodeId };
      scheduleRender();
      break;
    case "open-node":
      openNode(actionEl.dataset.nodeId);
      break;
    case "close-node":
      requestCloseNodePanel();
      break;
    case "edit-node":
      if (isNodeLocked(uiState.activeNodeId)) break;
      beginEditNode();
      break;
    case "save-node":
      saveNode();
      break;
    case "toggle-node-tag":
      if (callbacks.Store.isReadOnly?.()) break;
      toggleNodeTagFromButton(actionEl);
      break;
    case "set-node-date-preset":
      setNodeDatePreset(actionEl.dataset.preset);
      break;
    case "set-node-color": {
      const color = actionEl.dataset.color;
      const ctx = uiState.editingNodeContext;
      if (!color || !ctx) break;
      // Snapshot any in-flight DOM values first so we don't trample text the
      // user just typed when the swatch tap re-renders the panel.
      snapshotNodeEditDraftFromDom?.();
      const previous = uiState.nodeEditDraft && uiState.nodeEditDraft.docId === ctx.docId
        && uiState.nodeEditDraft.nodeId === ctx.nodeId
        ? uiState.nodeEditDraft
        : {};
      uiState.nodeEditDraft = {
        ...previous,
        docId: ctx.docId,
        nodeId: ctx.nodeId,
        color,
      };
      scheduleRender();
      break;
    }
    case "toggle-collapse":
      toggleCollapse(actionEl.dataset.nodeId);
      break;
    case "focus-node":
      callbacks.Store.setFocus(actionEl.dataset.nodeId);
      uiState.activeNodeId = null;
      // Breadcrumbs live on the outline AND the map tabs — refocusing from a
      // map should stay on that map, not bounce to the outline.
      if (!["outline", "mindmap", "bubble"].includes(uiState.tab)) {
        uiState.tab = "outline";
      }
      scheduleRender();
      break;
    case "add-child":
      openAddNodeForm(actionEl.dataset.nodeId);
      break;
    case "open-move-node":
      uiState.moveNodeId = actionEl.dataset.nodeId;
      scheduleRender();
      break;
    case "close-move-node":
      uiState.moveNodeId = null;
      scheduleRender();
      break;
    case "move-node-to-parent":
      moveNodeToParent(actionEl.dataset.parentId);
      break;
    case "request-delete-node":
      requestDeleteNode(actionEl.dataset.nodeId);
      break;
    case "open-doc":
      openDoc(actionEl.dataset.docId);
      break;
    case "link-badge": {
      // A link's badge (outline) or its buttons (node panel): open the
      // linked map, or ask for access when there is none.
      const node = callbacks.Store.find(actionEl.dataset.nodeId);
      if (canOpenLinkedMap(node)) {
        openDoc(node.linkedDocId);
      } else if (canRequestLinkAccess(node)) {
        const title = node.linkedDocTitle || node.name || t("app.fallback.thisDocument");
        uiState.confirm = {
          type: "request-link-access",
          nodeId: node.id,
          title: t("app.requestAccess.title"),
          message: t("app.requestAccess.message", { title }),
        };
        scheduleRender();
      }
      break;
    }
    case "open-folder":
      uiState.docsFolderId = actionEl.dataset.folderId || null;
      scheduleRender();
      break;
    case "up-folder":
      navigateUpFolder();
      break;
    case "create-doc":
      uiState.form = { type: "create-doc" };
      scheduleRender();
      break;
    case "create-folder":
      uiState.form = { type: "create-folder" };
      scheduleRender();
      break;
    case "close-form":
      uiState.form = null;
      uiState.formDraft = null;
      scheduleRender();
      break;
    case "submit-create-doc":
      createDocFromForm();
      break;
    case "submit-create-folder":
      createFolderFromForm();
      break;
    case "submit-rename-doc":
      renameDocFromForm();
      break;
    case "submit-rename-folder":
      renameFolderFromForm();
      break;
    case "submit-rename-node":
      renameNodeFromForm();
      break;
    case "submit-add-node":
      addNodeFromForm();
      break;
    case "open-doc-actions":
      uiState.docActionsId = actionEl.dataset.docId;
      scheduleRender();
      break;
    case "close-doc-actions":
      uiState.docActionsId = null;
      scheduleRender();
      break;
    case "doc-action-open":
      uiState.docActionsId = null;
      openDoc(actionEl.dataset.docId);
      break;
    case "doc-action-rename":
      uiState.form = { type: "rename-doc", docId: actionEl.dataset.docId };
      uiState.docActionsId = null;
      scheduleRender();
      break;
    case "doc-action-move": {
      const vault = callbacks.VaultStorage, entry = vault?.getDoc(actionEl.dataset.docId);
      uiState.docActionsId = null;
      scheduleRender();
      if (entry) moveToFolder({ vault, entry, onMoved: folderId => {
        uiState.docsFolderId = folderId; scheduleRender();
        callbacks.showToast?.(t("vault.map.moved"), { type: 'success' });
      } });
      break;
    }
    case "doc-action-duplicate":
      uiState.docActionsId = null;
      scheduleRender();
      cloneDoc(actionEl.dataset.docId).finally(scheduleRender);
      break;
    case "vault-export":
      handleExportData();
      break;
    case "vault-import":
      chooseVaultImport();
      break;
    case "doc-action-export":
      uiState.docActionsId = null;
      scheduleRender();
      exportMap(actionEl.dataset.docId);
      break;
    case "folder-action-move": {
      const vault = callbacks.VaultStorage;
      const entry = vault?.getIndex().folders.find(f => f.id === actionEl.dataset.folderId);
      uiState.folderActionsId = null;
      scheduleRender();
      if (entry) moveToFolder({ vault, entry, isFolder: true, onMoved: scheduleRender });
      break;
    }
    case "doc-action-delete":
      requestDeleteDoc(actionEl.dataset.docId);
      break;
    case "close-move-doc":
      uiState.moveDocId = null;
      scheduleRender();
      break;
    case "move-doc-to-folder":
      moveDocToFolder(actionEl.dataset.folderId);
      break;
    case "open-folder-actions":
      uiState.folderActionsId = actionEl.dataset.folderId;
      scheduleRender();
      break;
    case "close-folder-actions":
      uiState.folderActionsId = null;
      scheduleRender();
      break;
    case "folder-action-open":
      uiState.docsFolderId = actionEl.dataset.folderId || null;
      uiState.folderActionsId = null;
      scheduleRender();
      break;
    case "folder-action-new":
      uiState.form = { type: "create-folder", parentId: actionEl.dataset.folderId };
      uiState.folderActionsId = null;
      scheduleRender();
      break;
    case "folder-action-rename":
      uiState.form = { type: "rename-folder", folderId: actionEl.dataset.folderId };
      uiState.folderActionsId = null;
      scheduleRender();
      break;
    case "folder-action-delete":
      requestDeleteFolder(actionEl.dataset.folderId);
      break;
    case "related-notes":
      deps.openRelatedNotes(actionEl.dataset.nodeId);
      break;
    case "select-note":
      // Auto-save the current edit (if any) before switching to a different
      // note so we never silently drop in-progress text on tap.
      if (!ensurePendingEditsSavedBeforeNavigation()) break;
      uiState.activeNoteId = actionEl.dataset.noteId;
      uiState.activeNoteDocId = actionEl.dataset.noteDocId || null;
      uiState.noteSearchReveal = true;
      uiState.noteEditing = false;
      uiState.editingNoteContext = null;
      clearNoteEditDraft();
      scheduleRender();
      break;
    case "new-note":
      createNote();
      break;
    case "edit-note":
      if (callbacks.Store.isReadOnly?.()) break;
      beginEditNote();
      break;
    case "cancel-note-edit":
      requestCancelEditNote();
      break;
    case "save-note":
      saveNote(actionEl.dataset.noteId);
      break;
    case "delete-note":
      requestDeleteNote(actionEl.dataset.noteId);
      break;
    case "close-confirm":
      uiState.confirm = null;
      scheduleRender();
      break;
    case "confirm-action":
      runConfirmAction();
      break;
    case "board-column":
      uiState.boardColumnIndex = Number(actionEl.dataset.columnIndex || 0);
      scheduleRender();
      break;
    case "add-node-to-selected":
      openAddNodeToSelectedForm();
      break;
    case "collapse-selected-children":
      collapseSelectedChildren(actionEl.dataset.nodeId || null);
      break;
    case "expand-all-nodes":
      expandAllNodes();
      break;
    case "collapse-all-nodes":
      collapseAllNodes();
      break;
    case "toggle-board-title":
      uiState.expandedBoardNodeId =
        uiState.expandedBoardNodeId === actionEl.dataset.nodeId ? null : actionEl.dataset.nodeId;
      scheduleRender();
      break;
    case "change-card-status":
      if (callbacks.Store.isReadOnly?.()) break;
      uiState.statusNodeId = actionEl.dataset.nodeId;
      uiState.statusSourceTagId = actionEl.dataset.currentTagId || null;
      scheduleRender();
      break;
    case "close-status":
      uiState.statusNodeId = null;
      scheduleRender();
      break;
    case "set-card-status":
      setCardStatus(actionEl.dataset.nodeId, actionEl.dataset.tagId, actionEl.dataset.sourceTagId || uiState.statusSourceTagId);
      break;
    case "open-selected-properties":
      openSelectedProperties();
      break;

    case "gantt-row-zoom":
      adjustGanttRowHeight(actionEl.dataset.zoom);
      break;
    case "gantt-zoom-set": {
      const next = Number(actionEl.dataset.zoomIndex || 0);
      if (next !== uiState.ganttZoomIndex) {
        // Capture the centered day under the OLD dayWidth so the post-render
        // recenter math has a meaningful reference. The recenter avoids the
        // disorienting auto-snap-to-today that was firing on every zoom tap.
        deps.captureGanttCenterDay?.();
        uiState.ganttZoomIndex = next;
      }
      scheduleRender();
      break;
    }
    case "gantt-zoom-today":
      deps.setPendingGanttScrollToToday(true);
      scheduleRender();
      break;
    case "fit-desktop-view":
      fitMountedDesktopView();
      break;
    case "cycle-mm-layout":
      cycleMindmapLayoutMode();
      break;
    case "share-invite-submit":
      deps.shareInviteSubmit?.();
      break;
    case "share-remove-member":
      uiState.confirm = {
        type: "share-remove-member",
        userId: actionEl.dataset.userId,
        username: actionEl.dataset.username,
        linkOn: actionEl.dataset.linkOn === "true",
        title: t("mobile.confirm.removeUserTitle"),
        message: t("mobile.confirm.removeUser", { name: actionEl.dataset.username || t("mobile.confirm.thisUser") }),
      };
      scheduleRender();
      break;
    case "share-cancel-invitation":
      deps.shareCancelInvite?.(actionEl.dataset.invitationId);
      break;
    case "share-restore-access":
      // The member re-registered their encryption key (identity repair).
      uiState.confirm = {
        type: "share-restore-access",
        userId: actionEl.dataset.userId,
        username: actionEl.dataset.username,
        title: t("mobile.confirm.restoreAccessTitle"),
        message: t("mobile.confirm.restoreAccess", { name: actionEl.dataset.username || t("mobile.share.thisUser") }),
      };
      scheduleRender();
      break;
    case "share-link-create":
      deps.shareLinkCreate?.();
      break;
    case "share-link-copy":
      deps.shareLinkCopy?.();
      break;
    case "share-link-renew":
      uiState.confirm = { type: "share-link-renew", title: t("share.link.renew"), message: t("share.link.renewConfirm") };
      scheduleRender();
      break;
    case "share-link-off":
      uiState.confirm = { type: "share-link-off", title: t("share.link.turnOff"), message: t("share.link.turnOffConfirm") };
      scheduleRender();
      break;
    case "share-link-remove-held":
      uiState.confirm = {
        type: "share-link-remove-held",
        title: t("share.link.holder.remove"),
        message: t("share.link.holder.removeConfirm", { title: deps.getCurrentDocTitle?.() || t("common.untitled") }),
      };
      scheduleRender();
      break;
    case "share-stop-sharing":
      uiState.confirm = {
        type: "share-stop-sharing",
        title: t("mobile.confirm.stopSharingTitle"),
        message: t("mobile.confirm.stopSharing"),
      };
      scheduleRender();
      break;
    case "recover-share":
      deps.recoverShareFromNotification?.(
        actionEl.dataset.notificationId,
        actionEl.dataset.shareId,
        actionEl.dataset.shareTitle,
      );
      break;
    case "grant-access-request":
      deps.grantAccessRequest?.(
        actionEl.dataset.notificationId,
        actionEl.dataset.docId,
        actionEl.dataset.requesterUsername,
      );
      break;
    case "dismiss-notification":
      deps.dismissNotification?.(actionEl.dataset.notificationId);
      break;
    case "open-notifications":
      uiState.notificationsOpen = true;
      scheduleRender();
      break;
    case "close-notifications":
      uiState.notificationsOpen = false;
      scheduleRender();
      break;
    case "set-ui-theme":
      callbacks.setUITheme?.(actionEl.dataset.theme);
      scheduleRender();
      break;
    case "set-locale":
      // Saved on the device and the account, then the app reloads.
      chooseLanguage(actionEl.dataset.locale);
      break;
    case "install-pwa":
      promptInstall();
      break;
    case "mobile-logout":
      uiState.confirm = {
        type: "logout",
        title: t("common.logout"),
        message: t("mobile.confirm.logout"),
        action: () => callbacks.logout?.({ confirmed: true }),
      };
      scheduleRender();
      break;
    case "mobile-logout-everywhere":
      uiState.confirm = {
        type: "logout-everywhere",
        title: t("account.logoutAll.title"),
        message: t("account.logoutAll.message"),
        action: () => callbacks.logout?.({ everywhere: true, confirmed: true }),
      };
      scheduleRender();
      break;
    case "regenerate-recovery":
      // Same flow the desktop Profile → Security → Regenerate button runs.
      // The phrase dialog it opens lives outside the mobile shell (it is a
      // body-level overlay), so close our sheet first to avoid stacking.
      uiState.userInfoOpen = false;
      uiState.confirm = {
        type: "regenerate-recovery",
        title: t("account.phrase.newTitle"),
        message: t("mobile.confirm.recovery"),
        action: async () => {
          try {
            await callbacks.regenerateRecoveryPhrase?.();
          } catch (err) {
            console.error("[Mobile] Regenerate recovery phrase failed:", err);
            callbacks.showToast?.(
              t("mobile.account.recoveryFailed", { error: err?.message || err }),
              { type: "error" },
            );
          }
        },
      };
      scheduleRender();
      break;
    case "attach-photo":
      pickAttachmentPhoto();
      break;
    case "attach-file":
      pickAttachmentFile();
      break;
    case "accept-invitation": {
      const invitationId = actionEl.dataset.invitationId;
      if (!invitationId) break;
      // Optimistic disable: accepting does key unwrapping + a vault write, so
      // it is not instant. Without this the row stays tappable and a double
      // tap fires two accepts.
      actionEl.disabled = true;
      actionEl.textContent = t("mobile.invite.accepting");
      (async () => {
        try {
          await callbacks.acceptInvitation?.(invitationId);
          callbacks.showToast?.(t("mobile.invite.accepted"), { type: "success" });
        } catch (err) {
          // The user chose "Later" in the join dialog: not an error.
          if (err?.code === "cancelled") {
            actionEl.disabled = false;
            actionEl.textContent = t("share.inbox.accept");
            scheduleRender();
            return;
          }
          console.error("[Mobile] Accept invitation failed:", err);
          callbacks.showToast?.(
            t("share.inbox.acceptError", { error: err?.message || err }),
            { type: "error" },
          );
        }
        scheduleRender();
      })();
      break;
    }
    case "decline-invitation": {
      const declineId = actionEl.dataset.invitationId;
      if (!declineId) break;
      uiState.confirm = {
        type: "decline-invitation",
        title: t("share.inbox.declineTitle"),
        message: t("mobile.invite.declineConfirm"),
        action: async () => {
          try {
            await callbacks.declineInvitation?.(declineId);
            callbacks.showToast?.(t("mobile.invite.declined"), { type: "info" });
          } catch (err) {
            console.error("[Mobile] Decline invitation failed:", err);
            callbacks.showToast?.(
              t("mobile.invite.declineFailed", { error: err?.message || err }),
              { type: "error" },
            );
          }
        },
      };
      scheduleRender();
      break;
    }
    case "ignore-inviter": {
      // Every invitation from this person, these and later ones; undone from
      // the account's settings (Alex, 2026-10-26).
      const ignoreId = actionEl.dataset.invitationId;
      if (!ignoreId) break;
      const name = actionEl.dataset.inviter || t("mobile.invite.someone");
      uiState.confirm = {
        type: "ignore-inviter",
        title: t("share.inbox.ignoreTitle"),
        message: t("share.inbox.ignoreConfirm", { name }),
        action: async () => {
          try {
            await callbacks.ignoreInviter?.(ignoreId);
            callbacks.showToast?.(t("share.inbox.ignored", { name }), { type: "info" });
          } catch (err) {
            console.error("[Mobile] Ignoring invitations failed:", err);
            callbacks.showToast?.(t("share.inbox.ignoreFailed"), { type: "error" });
          }
        },
      };
      scheduleRender();
      break;
    }
    case "delete-account":
      // Delegates to the shared deletion modal (body-level overlay, kept
      // visible on mobile by interfaces/classic/styles/mobile.css).
      uiState.userInfoOpen = false;
      scheduleRender();
      callbacks.showDeletionModal?.();
      break;
    default:
      break;
  }
}
